import type { PipeIntegrationDef, PipeIntegration } from '@render/integrations'
import type { PipeInterface } from '@render/composables/usePipe'
import { watch } from 'vue'
import { configureYunoSdk, loadYunoSdk, YUNO_ORIGINS } from '@render/integrations/yuno/yunoSdk'
import { preCreateYunoSession } from '@render/integrations/yuno/yunoPreCreate'

// Long enough that dragging through the plan list settles before we post, short enough that the
// session is already in flight by the time the visitor reaches the CTA.
const PRE_CREATE_DEBOUNCE_MS = 100

// Yuno pre-warm integration — warms the CDN/API connections and loads the SDK
// bundle before the user reaches the checkout page. Without it, YunoCheckout's
// mount pays for a cold third-party origin (DNS + TLS + TCP, then download,
// parse and execute) on the critical path, and it pays for it AFTER the session
// POST has resolved, because useYuno only reaches loadYunoSdk() inside
// renderYunoCheckout. Auto-activated at build time when YunoCheckout is used in
// the pipe (see builder/lib/pages.ts getActiveIntegrations), with the SDK version
// to load (`sdkVersion`) from the CHECKOUT row's `yuno` part.
//
// There is deliberately no rel=preload here, unlike the Primer integration.
// Primer's npm package is a loader shim whose real bundle (PrimerCoreSDK.min.js)
// is fetched later by showUniversalCheckout, so preloading that second URL is
// the only way to warm it. Yuno's package IS the loader: loadYunoSdk() injects
// the one bundle through the package's own loadScript(). A hand-written preload
// would have to hardcode a URL the package derives itself from its LATEST_VERSION
// constant, so it would silently point at the wrong version on the next package
// bump — warming a bundle nobody then loads.
const yuno: PipeIntegration = (): PipeIntegrationDef => {
  return {
    setup(pipe: PipeInterface, config?: { sdkVersion?: string }) {
      if (typeof window === 'undefined') return

      configureYunoSdk(config?.sdkVersion)

      // Warm TCP/TLS to Yuno's origins immediately on setup — before any user
      // interaction, and before the email watcher below can fire.
      for (const { href, anonymous } of YUNO_ORIGINS) {
        const dns = document.createElement('link')
        dns.rel = 'dns-prefetch'
        dns.href = href
        document.head.appendChild(dns)

        const preconnect = document.createElement('link')
        preconnect.rel = 'preconnect'
        preconnect.href = href
        if (anonymous) preconnect.crossOrigin = 'anonymous'
        document.head.appendChild(preconnect)
      }

      // The SDK chunk load DOES retry. An offline visitor fails it right at the
      // email step (this watcher's trigger); without a retry the checkout would
      // later mount against a dead loader. loadYunoSdk never caches a rejection,
      // so re-running loadAssets re-attempts the load.
      let sdkLoaded = false
      let onlineRetryArmed = false

      const loadAssets = () => {
        if (sdkLoaded) return

        // Resolve the SDK now so the script is already executed and
        // `window.SdkPayments` is live by the time YunoCheckout mounts and calls
        // loadYunoSdk() itself. Injecting the bundle does not initialize
        // anything, touch the mount containers or create a session — it is inert
        // until Yuno.initialize() runs.
        loadYunoSdk()
          .then(() => {
            sdkLoaded = true
          })
          .catch((err) => {
            // Offline (or a CDN hiccup) during the pre-warm. loadYunoSdk has
            // already cleared its singleton, so retry the moment the browser
            // reports connectivity back. The checkout mount retries on its own
            // too — this just front-loads the recovery.
            console.warn('[yuno] SDK pre-warm failed — will retry when back online', err)
            if (onlineRetryArmed) return
            onlineRetryArmed = true
            window.addEventListener(
              'online',
              () => {
                onlineRetryArmed = false
                loadAssets()
              },
              { once: true },
            )
          })
      }

      // Trigger once email is known — user is past intake, heading to checkout.
      // Fires immediately for returning users with email in persisted state.
      watch(
        () => pipe.getData('var.global.email'),
        (email) => {
          if (typeof email === 'string' && email.trim().length > 0) loadAssets()
        },
        { immediate: true },
      )

      // Pre-create the checkout session once an offer is selected, so YunoCheckout's mount reads it
      // from cache instead of blocking on the ~400ms POST. See yunoPreCreate for the payload and why
      // this cannot charge anyone.
      //
      // Presence-based, not change-based: it fires whenever a selected offer EXISTS. That covers the
      // default option (OfferSelectComponent writes it in its own onMounted) and a selection restored
      // from localStorage before this watcher existed — hence `immediate`. Debounced so dragging
      // through the plan list settles before we post, and deep because the selection lands as a
      // nested write.
      let preCreateTimer: ReturnType<typeof setTimeout> | null = null

      watch(
        () => pipe.getData('var.global.selectedOfferGroup'),
        (selectedOfferGroup) => {
          if (!selectedOfferGroup) return
          if (preCreateTimer !== null) clearTimeout(preCreateTimer)
          preCreateTimer = setTimeout(() => {
            preCreateTimer = null
            // PROTOTYPE: pipelineOfferType is hardcoded. It is authored per-checkout-instance
            // (`fields.pipelineOfferType` on YunoCheckout) and lives nowhere in pipe state, so the
            // funnel cannot read the real value. If a pipe's checkout is authored as anything other
            // than BASIC the fingerprints differ and the mount simply refetches — no benefit, no harm.
            void preCreateYunoSession(pipe, 'BASIC')
          }, PRE_CREATE_DEBOUNCE_MS)
        },
        { deep: true, immediate: true },
      )
    },
  }
}

export default yuno
